"use client";

import * as React from "react";
import Link from "next/link";
import { ArrowRight, BookOpen, Sparkles } from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";
import { ACCENT_CYCLE, type AccentColor } from "./lib/shared";
import { NamedIcon } from "./icon-component";

const ACCENT: Record<string, { text: string; bg: string; border: string; ring: string; pill: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/30", ring: "ring-red/20", pill: "glass-tinted-red border-red/20" },
  coral: { text: "text-coral", bg: "from-coral/10", border: "border-coral/30", ring: "ring-coral/20", pill: "glass-tinted-coral border-coral/20" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/30", ring: "ring-purple/20", pill: "glass-tinted-purple border-purple/20" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/30", ring: "ring-cyan/20", pill: "glass-tinted-cyan border-cyan/20" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/30", ring: "ring-denim/20", pill: "glass-tinted-denim border-denim/20" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/30", ring: "ring-emerald/20", pill: "glass-tinted-emerald border-emerald/20" },
  lime: { text: "text-lime", bg: "from-lime/10", border: "border-lime/30", ring: "ring-lime/20", pill: "glass-tinted-lime border-lime/20" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/30", ring: "ring-amber-gold/20", pill: "glass-tinted-amber-gold border-amber-gold/20" },
};

interface CategorySummary {
  slug: string;
  label: string;
  overline: string;
  description: string;
  longDescription: string;
  iconName: string;
  accent: AccentColor;
}

interface ArticleSummary {
  categorySlug: string;
  categoryLabel: string;
  slug: string;
  title: string;
  excerpt: string;
  iconName: string;
  readingTime: number;
}

export interface InsightsHubViewProps {
  categories: CategorySummary[];
  counts: Record<string, number>;
  totalArticles: number;
  featured: ArticleSummary[];
}

export function InsightsHubView({ categories, counts, totalArticles, featured }: InsightsHubViewProps) {
  return (
    <>
      <PageHero
        icon={BookOpen}
        overline="The Insights Library"
        overlineClassName="text-red"
        title={
          <>
            The Operating Library of <span className="text-gradient-premium">Dr. Jyoti Kush.</span>
          </>
        }
        subtitle="One hundred and ten essays and case studies on leadership as diagnosis, operational excellence, sovereign-scale strategy, governance transformation, and the talent the world overlooks. The same operating system that runs sovereign-level engagements — written down."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Insights" }]}
        badge={`${totalArticles} articles across ${categories.length} domains`}
      />

      <Section background="default" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.red.pill} mb-5`}>
              <Sparkles className="size-3.5 text-red" />
              <span className="text-overline text-red">About This Library</span>
            </div>
            <h2 className="text-display-md mb-5">
              Documentation of an operating system.
            </h2>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              These are not essays written for publication. They are the documentation of an operating system proven across sovereign-level engagements in eighteen countries. The clinical method applied to leadership, the architecture of operational excellence, the discipline of sovereign-scale strategy, and the operating case for the talent the world overlooks.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              The library is organized into nine domains. Each domain contains a small, focused set of essays — written to be read in order, each one building on the previous. Together, they form the most complete articulation of how excellent things actually run.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              { metric: String(totalArticles), label: "Articles", sub: "Across nine domains", accent: "red" },
              { metric: String(categories.length), label: "Domains", sub: "The full operating range", accent: "purple" },
              { metric: "18", label: "Countries", sub: "The proven scope", accent: "cyan" },
              { metric: "0", label: "Incidents", sub: "15+ years clean record", accent: "emerald" },
              { metric: "10+", label: "Years", sub: "Of verified practice", accent: "amber-gold" },
              { metric: "70", suffix: "%", label: "Women-led", sub: "The operating case for the talent the world overlooks", accent: "lime" },
            ].map((s) => {
              const a = ACCENT[s.accent];
              return (
                <div
                  key={s.label}
                  className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border}`}
                >
                  <div className={`text-display-sm font-bold ${a.text} leading-none mb-2`}>
                    {s.metric}{s.suffix || ""}
                  </div>
                  <div className="text-caption font-bold uppercase tracking-wider mb-1">{s.label}</div>
                  <div className="text-caption text-muted-foreground">{s.sub}</div>
                </div>
              );
            })}
          </div>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-4`}>
            <BookOpen className="size-3.5 text-purple" />
            <span className="text-overline text-purple">The Nine Domains</span>
          </div>
          <h2 className="text-display-md mb-4">Choose a domain.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Each domain is a complete body of work. Begin anywhere — every essay is written to stand on its own, but together they form a unified operating philosophy.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {categories.map((category) => {
            const a = ACCENT[category.accent];
            const count = counts[category.slug] || 0;
            return (
              <Link
                key={category.slug}
                href={`/insights/${category.slug}/`}
                className="group block h-full"
              >
                <div className={`relative h-full glass-card rounded-2xl p-6 overflow-hidden bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}>
                  <div aria-hidden className="absolute -top-10 -right-10 size-32 rounded-full bg-current opacity-[0.05] blur-2xl group-hover:scale-150 transition-transform duration-700" />
                  <div className="relative">
                    <div className="flex items-start justify-between mb-4">
                      <div className={`size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center shadow-lg`}>
                        <NamedIcon name={category.iconName} className="size-6 text-white" />
                      </div>
                      <span className={`text-overline ${a.text} opacity-70`}>
                        {String(count).padStart(2, "0")} articles
                      </span>
                    </div>
                    <span className={`text-overline ${a.text} mb-1 block`}>
                      {category.overline}
                    </span>
                    <h3 className={`text-display-sm mb-2 ${a.text}`}>
                      {category.label}
                    </h3>
                    <p className="text-body-sm text-muted-foreground leading-relaxed mb-4">
                      {category.description}
                    </p>
                    <div className={`inline-flex items-center gap-1.5 text-caption font-semibold ${a.text} group-hover:gap-2.5 transition-all`}>
                      Explore the domain <ArrowRight className="size-3" />
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-4`}>
            <Sparkles className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">Recently Featured</span>
          </div>
          <h2 className="text-display-md mb-4">Start with these.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Three essays that establish the foundation. Begin here, then move into the domain that addresses the specific operational question being asked.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          {featured.map((article, i) => {
            const accent = ACCENT_CYCLE[i % ACCENT_CYCLE.length];
            const a = ACCENT[accent];
            return (
              <Link
                key={`${article.categorySlug}/${article.slug}`}
                href={`/insights/${article.categorySlug}/${article.slug}/`}
                className="group block h-full"
              >
                <div className={`relative h-full glass-card rounded-2xl p-5 overflow-hidden bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}>
                  <div aria-hidden className="absolute -top-10 -right-10 size-32 rounded-full bg-current opacity-[0.05] blur-2xl group-hover:scale-150 transition-transform duration-700" />
                  <div className="relative">
                    <div className="flex items-start justify-between mb-3">
                      <div className={`size-10 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                        <NamedIcon name={article.iconName} className="size-5 text-white" />
                      </div>
                      <span className={`text-overline ${a.text} opacity-70`}>
                        {article.readingTime} min read
                      </span>
                    </div>
                    <span className={`text-overline ${a.text} mb-1 block`}>
                      {article.categoryLabel}
                    </span>
                    <h3 className={`text-heading-md mb-2 ${a.text}`}>
                      {article.title}
                    </h3>
                    <p className="text-caption text-muted-foreground leading-relaxed mb-3 line-clamp-3">
                      {article.excerpt}
                    </p>
                    <div className={`inline-flex items-center gap-1.5 text-caption font-semibold ${a.text} group-hover:gap-2.5 transition-all`}>
                      Read the essay <ArrowRight className="size-3" />
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-20 lg:py-28">
        <div className="relative">
          <div aria-hidden className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-red/15 blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-purple/15 blur-3xl pointer-events-none" />
          <div className="relative glass-strong rounded-3xl p-8 md:p-12 lg:p-16 gradient-border text-center">
            <BookOpen className="size-10 text-red mx-auto mb-4" />
            <h2 className="text-display-md mb-3">Build with this library.</h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              The essays are not the destination. They are the operating manual. For executive advisory, operational consulting, or speaking engagements that apply this operating system to a specific organizational challenge — begin the engagement.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/contact/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-gradient-sovereign text-white font-semibold shadow-lg hover:scale-105 transition-transform"
              >
                Begin an Engagement
                <ArrowRight className="size-4" />
              </Link>
              <Link
                href="/expertise/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md glass font-semibold hover:scale-105 transition-transform"
              >
                Explore the Expertise Domains
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <RelatedPages
        title="Adjacent Reading"
        items={[
          { title: "About Dr. Jyoti Kush", description: "The full arc — from medicine to sovereign-scale operations.", href: "/about/", icon: BookOpen, accent: "red" },
          { title: "The Expertise Domains", description: "The five interconnected domains of practice.", href: "/expertise/", icon: BookOpen, accent: "purple" },
          { title: "The 70% Project", description: "The flagship initiative documenting the operating case for the talent the world overlooks.", href: "/the-70-percent-project/", icon: BookOpen, accent: "emerald" },
          { title: "The Operator&apos;s Notebook", description: "Biweekly notes on how excellent things actually run.", href: "/the-operators-notebook/", icon: BookOpen, accent: "amber-gold" },
        ]}
      />
    </>
  );
}
